import { SortIcon } from "@bookingcom/bui-assets-react/streamline";
import { Chip, DropdownMenu, Stack, Text } from "@bookingcom/bui-react";
import { SBSortingOrder } from "@bookingcom/flights-searchbox";
import { TranslationMessage } from "@bookingcom/lingojs-core/build/t";
import { t, useI18n } from "@bookingcom/lingojs-react";
import useCreateUrl from "../../../../hooks/useCreateUrl";
import useEventTracking from "../../../../hooks/useEventTracking";
import useGlobalContext from "../../../../hooks/useGlobalContext";
import React, { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import flights_web_remove_fastest_sorting from "../../../../utils/experiments/funnel/flights_web_remove_fastest_sorting";
import { getSearchSortingOptions } from "../../../../store/searchResults/selectors";
import { useSelector } from "react-redux";

type Sort = Extract<SBSortingOrder, "BEST" | "CHEAPEST" | "FASTEST" | "GENIUS_DEALS">;

const dropdownAttributes = { style: { width: 360 } };

const SearchSortSelector = ({ isResponsiveFixesSm }: { isResponsiveFixesSm?: boolean }) => {
  const i18n = useI18n();
  const history = useHistory();
  const { enhanceURLSearchParams } = useCreateUrl();
  const { requestId } = useGlobalContext();

  const trackV2 = useEventTracking("search_results", requestId);
  const removeFastestSorting = !!flights_web_remove_fastest_sorting.trackWithDefaultStage();

  const [sorting, setSorting] = useState<Sort>(() => {
    const query = new URLSearchParams(location.search);
    return (query.get("sort") as Sort) || "BEST";
  });

  const sortingOptions = useSelector(getSearchSortingOptions);
  const isGeniusDealsEnabled = useMemo(() => {
    return sortingOptions?.some(({ id, enabled }) => id === "genius_deals" && enabled) || false;
  }, [sortingOptions]);

  const sortLabels: Partial<
    Record<Sort, { title: TranslationMessage<any, any>; subtitle?: TranslationMessage<any, any> }>
  > = {
    BEST: { title: t("flights_search_sort_best"), subtitle: t("flights_search_sort_best_tooltip") },
    CHEAPEST: { title: t("flights_search_sort_cheapest") },
    ...(isGeniusDealsEnabled
      ? {
          GENIUS_DEALS: { title: t("flights_sort_by_genius_title"), subtitle: t("flights_sort_by_genius_tooltip") }
        }
      : {}),
    ...(removeFastestSorting ? {} : { FASTEST: { title: t("flights_search_sort_fastest") } })
  };
  const sortLabel = sortLabels[sorting];
  const sortName = sortLabel ? i18n.trans(sortLabel.title) : "";

  const handleItemChoose = useCallback(
    (sort: Sort) => {
      switch (sort) {
        case "BEST":
          flights_web_remove_fastest_sorting.goals.selectedBestSorting();
          break;
        case "CHEAPEST":
          flights_web_remove_fastest_sorting.goals.selectedCheapestSorting();
          break;
        case "GENIUS_DEALS":
          flights_web_remove_fastest_sorting.goals.selectedGeniusSorting();
          break;
        case "FASTEST":
          flights_web_remove_fastest_sorting.goals.selectedFastestSorting();
      }

      setSorting(sort);

      setTimeout(() => {
        const params = new URLSearchParams(location.search);
        params.set("sort", sort);
        params.delete("page");

        enhanceURLSearchParams(params);
        history.replace(
          {
            pathname: location.pathname,
            search: params.toString()
          },
          { hideLoader: true }
        );
      });
      trackV2("click_sort_by_link", sort);
    },
    [enhanceURLSearchParams, history, trackV2]
  );

  const items = Object.entries(sortLabels).map(([sort, { title, subtitle }]) => ({
    text: (
      <Stack gap={0}>
        <Text>{i18n.trans(title)}</Text>
        {subtitle && (
          <Text variant="small_1" color={"neutral_alt"}>
            {i18n.trans(subtitle)}
          </Text>
        )}
      </Stack>
    ),
    onChoose: () => handleItemChoose(sort as Sort)
  }));

  return (
    <DropdownMenu
      position={isResponsiveFixesSm ? "bottom-start" : "bottom-end"}
      items={items}
      attributes={isResponsiveFixesSm ? {} : dropdownAttributes}
    >
      {(attributes) => (
        <Chip
          variant="action"
          label={i18n.trans(t("flights_sort_by_name", { variables: { sort_name: sortName } }))}
          checked={true}
          icon={<SortIcon />}
          attributes={attributes}
        />
      )}
    </DropdownMenu>
  );
};

export default SearchSortSelector;
